import { useState } from 'react'
import { Button, Form, Input, Select, DatePicker, Tag, Popconfirm, InputNumber, App } from 'antd'
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { workOrdersApi, serviceMastersApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { WorkOrder } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default', APPROVED: 'green', IN_PROGRESS: 'processing',
  COMPLETED: 'success', CANCELLED: 'error',
}

export default function WorkOrderPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const canCreate = usePermission('wo.create')
  const canApprove = usePermission('wo.approve')

  const { data, isLoading } = useQuery({
    queryKey: ['work-orders', plantFilter, statusFilter],
    queryFn: () => workOrdersApi.list({ plant_id: plantFilter, status: statusFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })
  const { data: services } = useQuery({ queryKey: ['service-masters'], queryFn: () => serviceMastersApi.list() })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []
  const serviceOptions = services?.map(s => ({ label: s.service_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        start_date: values.start_date ? dayjs(values.start_date as string).format('YYYY-MM-DD') : undefined,
        end_date: values.end_date ? dayjs(values.end_date as string).format('YYYY-MM-DD') : undefined,
      }
      return workOrdersApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['work-orders'] }); message.success('Work Order created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => workOrdersApi.approve(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['work-orders'] }); message.success('Work Order approved') },
    onError: () => message.error('Approval failed'),
  })

  const openCreate = () => {
    form.resetFields()
    form.setFieldsValue({ total_value: 0, advance_pct: 0, lines: [{ description: '', quantity: 1, uom: 'DAY', rate: 0, tax_pct: 18 }] })
    setDrawerOpen(true)
  }

  const columns = [
    { title: 'WO No', dataIndex: 'wo_no', key: 'wo_no', width: 120 },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
    { title: 'Total Value', dataIndex: 'total_value', key: 'total_value', width: 120,
      render: (v: string) => `₹${Number(v).toLocaleString('en-IN')}` },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 120,
      render: (_: unknown, r: WorkOrder) =>
        r.status === 'DRAFT' && canApprove ? (
          <Popconfirm title="Approve this Work Order?" onConfirm={() => approveMutation.mutate(r.id)}>
            <Button size="small" type="primary">Approve</Button>
          </Popconfirm>
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Work Orders"
        subtitle="Service purchase orders — manage vendor work execution"
        extra={canCreate && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New WO</Button>}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
        <Select allowClear placeholder="Status"
          options={['DRAFT', 'APPROVED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'].map(s => ({ label: s, value: s }))}
          onChange={v => setStatusFilter(v)} style={{ width: 150 }}
        />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Work Order"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
        width={800}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true }]}>
          <Select options={supplierOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="scope_of_work" label="Scope of Work" rules={[{ required: true }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <div style={{ display: 'flex', gap: 8 }}>
          <Form.Item name="start_date" label="Start Date" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end_date" label="End Date" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </div>
        <Form.Item name="total_value" label="Total Value (₹)" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
        <Form.Item name="advance_pct" label="Advance %">
          <InputNumber style={{ width: '100%' }} min={0} max={100} />
        </Form.Item>

        <div style={{ fontWeight: 600, marginBottom: 8, marginTop: 8 }}>Service Lines</div>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }) => (
                <div key={key} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'flex-start' }}>
                  <Form.Item name={[name, 'description']} style={{ flex: 2, marginBottom: 0 }} rules={[{ required: true }]}>
                    <Input placeholder="Description" />
                  </Form.Item>
                  <Form.Item name={[name, 'quantity']} style={{ width: 80, marginBottom: 0 }}>
                    <InputNumber placeholder="Qty" min={0.001} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'uom']} style={{ width: 80, marginBottom: 0 }}>
                    <Input placeholder="UOM" />
                  </Form.Item>
                  <Form.Item name={[name, 'rate']} style={{ width: 100, marginBottom: 0 }}>
                    <InputNumber placeholder="Rate" min={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'tax_pct']} style={{ width: 80, marginBottom: 0 }}>
                    <InputNumber placeholder="GST%" min={0} max={28} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name={[name, 'service_id']} style={{ flex: 1, marginBottom: 0 }}>
                    <Select allowClear placeholder="Service" options={serviceOptions} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red', marginTop: 8 }} />
                </div>
              ))}
              <Button type="dashed" onClick={() => add({ description: '', quantity: 1, uom: 'DAY', rate: 0, tax_pct: 18 })} icon={<PlusOutlined />} block>
                Add Line
              </Button>
            </>
          )}
        </Form.List>
      </FormDrawer>
    </div>
  )
}
